import { useState, useCallback, useRef } from 'react';
import { chatAPI } from '../services/api';
import stateManager from '../services/stateManager';

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [currentMission, setCurrentMission] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Building your personalised insights...');
  const loadingHistoryRef = useRef(false);

  // Pre-fetch loading message to avoid delay when showing loading screen
  // NOTE: We use a generic fallback message here instead of calling the API
  // because the API auto-marks queued facts as displayed, which would consume
  // celebration facts before the user has a chance to see them in QueuedFactDisplay
  const prefetchLoadingMessage = useCallback(async () => {
    try {
      // Don't call the API - just set a nice generic message
      // QueuedFactDisplay will handle showing any queued facts separately
      const fallbackMessages = [
        'Building your personalised insights...',
        'Preparing your next step...',
        'Crafting a thoughtful response...',
        'Considering your journey...',
        'Thinking this through...'
      ];

      // Rotate through messages
      const index = Math.floor(Date.now() / 30000) % fallbackMessages.length;
      setLoadingMessage(fallbackMessages[index]);
      console.log('[useChat] Set generic loading message (not consuming queued facts)');
    } catch (error) {
      console.error('[useChat] Failed to set loading message:', error);
      // Set a fallback message on error
      setLoadingMessage('Keep going! Every step counts.');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    // Prevent concurrent loads (React Strict Mode calls effects twice)
    if (loadingHistoryRef.current) {
      return null;
    }

    loadingHistoryRef.current = true;
    setIsLoading(true);
    try {
      // Check cache first
      const cachedMessages = stateManager.getMessages();
      const cachedConversationId = stateManager.getConversationId();
      const cachedMission = stateManager.getCurrentMission();

      if (cachedMessages && cachedConversationId) {
        // console.log('[useChat] Using cached messages');
        setMessages(cachedMessages);
        setConversationId(cachedConversationId);
        setCurrentMission(cachedMission);
        setIsLoading(false);
        loadingHistoryRef.current = false;
        // Pre-fetch loading message for next time
        prefetchLoadingMessage();
        return cachedConversationId;
      }

      // Fetch if cache miss or expired
      const data = await chatAPI.getHistory();
      stateManager.setMessages(data.messages || []);
      stateManager.setConversationId(data.conversationId);
      stateManager.setCurrentMission(data.currentMission || null);
      setMessages(data.messages || []);
      setConversationId(data.conversationId);
      setCurrentMission(data.currentMission || null);

      // Pre-fetch loading message for next interaction
      prefetchLoadingMessage();

      // Return the new conversationId so caller can use it immediately
      return data.conversationId;
    } catch (error) {
      console.error('[useChat] Failed to load history:', error);
      return null;
    } finally {
      setIsLoading(false);
      loadingHistoryRef.current = false;
    }
  }, [prefetchLoadingMessage]);

  const sendMessage = async (message, action = null) => {
    if (!message.trim()) return;

    console.log('🟢 setIsLoading(TRUE)');
    setIsLoading(true);

    try {
      // Use NON-streaming endpoint (disable streaming for debugging)
      const data = await chatAPI.sendMessage(message, action);

      // Update messages with server response
      setMessages(prev => {
        let filtered = [...prev];

        // If we're receiving new plan messages, REMOVE all old plan messages first
        if (data.planMessages && data.planMessages.length > 0) {
          filtered = filtered.filter(m => !m.content.includes('<TodoPlan:'));
        }

        const newMessages = [
          ...filtered,
          data.userMessage
        ];

        // Add assistant message only if it exists (might not exist if Claude only used tools)
        if (data.assistantMessage) {
          newMessages.push(data.assistantMessage);
        }

        // Add new plan messages
        if (data.planMessages && data.planMessages.length > 0) {
          newMessages.push(...data.planMessages);
        }

        // Add tool responses (final Claude response after tool execution)
        if (data.toolResponses && data.toolResponses.length > 0) {
          newMessages.push(...data.toolResponses);

          // Check if tool responses contain embedded todo widgets
          // If so, refresh todos from server to update singleton cache
          const hasEmbeddedWidgets = data.toolResponses.some(msg =>
            msg.content && msg.content.includes('<TodoWidget:')
          );

          if (hasEmbeddedWidgets && conversationId) {
            console.log('[useChat] Tool response contains embedded widgets - refreshing todos from server');

            // Reload todos in background (don't block UI)
            import('../services/api').then(({ todosAPI }) => {
              todosAPI.getTodos(conversationId)
                .then(freshTodos => {
                  console.log('[useChat] Refreshed todos from server after embedded widget');
                  stateManager.setTodos(freshTodos);
                })
                .catch(err => {
                  console.error('[useChat] Failed to refresh todos:', err);
                });
            });
          }
        }

        // Add new widget messages and refresh todos from server
        if (data.widgetMessages && data.widgetMessages.length > 0) {
          newMessages.push(...data.widgetMessages);

          // Always refresh todos from server when widgets appear
          // This ensures accurate state display
          if (conversationId) {
            // console.log('[useChat] Widget messages received - refreshing todos from server');

            // Reload todos in background (don't block UI)
            import('../services/api').then(({ todosAPI }) => {
              todosAPI.getTodos(conversationId)
                .then(freshTodos => {
                  // console.log('[useChat] Refreshed todos from server');
                  stateManager.setTodos(freshTodos);
                })
                .catch(err => {
                  console.error('[useChat] Failed to refresh todos:', err);
                });
            });
          }
        }

        // Refresh todos if they were modified via add/edit/remove tools
        if (data.todosUpdated && conversationId) {
          console.log('[useChat] Todos updated via tools - refreshing from server');

          // Reload todos in background (don't block UI)
          import('../services/api').then(({ todosAPI }) => {
            todosAPI.getTodos(conversationId)
              .then(freshTodos => {
                console.log('[useChat] Refreshed todos from server after update');
                stateManager.setTodos(freshTodos);
                // Trigger a re-render by updating state
                // This will cause any components using todos to refresh
                window.dispatchEvent(new CustomEvent('todosRefreshed', { detail: freshTodos }));
              })
              .catch(err => {
                console.error('[useChat] Failed to refresh todos:', err);
              });
          });
        }

        // Update cache
        stateManager.setMessages(newMessages);

        return newMessages;
      });

      setConversationId(data.conversationId);
      stateManager.setConversationId(data.conversationId);
    } catch (error) {
      console.error('[useChat] Failed to send message:', error);
      throw error;
    } finally {
      console.log('🔴 setIsLoading(FALSE)');
      setIsLoading(false);
      // Pre-fetch next loading message after response completes
      prefetchLoadingMessage();
    }
  };

  const clearConversation = async () => {
    try {
      await chatAPI.clearConversation();
      setMessages([]);
      setConversationId(null);
      stateManager.clearAll();
    } catch (error) {
      console.error('Failed to clear conversation:', error);
    }
  };

  return {
    messages,
    isLoading,
    conversationId,
    currentMission,
    loadingMessage,
    sendMessage,
    clearConversation,
    loadHistory
  };
}
